import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { notificationsAPI } from '../services/api';

const Notifications = () => {
    const { isDark } = useTheme();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            // Fetch more notifications for the full page view
            const response = await notificationsAPI.getNotifications({ limit: 50 });
            setNotifications(response.data.notifications);
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            await notificationsAPI.markAsRead(id);
            setNotifications(prev => prev.map(n =>
                n.id === id ? { ...n, read: true } : n
            ));
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationsAPI.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            fetchNotifications(); // Refresh to ensure backend sync
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'lead_auto_responded': return 'smart_toy';
            case 'lead_replied': return 'chat';
            case 'system_alert': return 'warning';
            default: return 'notifications';
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'lead_auto_responded': return 'text-cyan';
            case 'lead_replied': return 'text-emerald-500';
            case 'system_alert': return 'text-amber-500';
            default: return isDark ? 'text-light' : 'text-darkTeal';
        }
    };

    if (loading) {
        return (
            <div className={`p-8 flex justify-center ${isDark ? 'text-light' : 'text-darkTeal'}`}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-current"></div>
            </div>
        );
    }

    return (
        <div className={`p-4 md:p-6 w-full min-h-screen ${isDark ? 'text-light' : 'text-darkTeal'}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-left">Notifications</h1>
                    <p className={`text-sm mt-1 text-left ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>
                        View and manage your alerts
                    </p>
                </div>
                <button
                    onClick={markAllAsRead}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors w-full md:w-auto text-center ${isDark
                        ? 'bg-navy border border-light/20 hover:bg-light/10 text-cyan'
                        : 'bg-white border border-teal/20 hover:bg-teal/5 text-primary'
                        }`}
                >
                    Mark All as Read
                </button>
            </div>

            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-navy/50 border-light/10' : 'bg-white border-teal/10 shadow-sm'}`}>
                {notifications.length === 0 ? (
                    <div className="p-12 text-center opacity-60">
                        <span className="material-symbols-outlined text-4xl mb-3 block">notifications_off</span>
                        <p>No notifications to display</p>
                    </div>
                ) : (
                    <div className="divide-y divide-current/10">
                        {notifications.map(notification => (
                            <div
                                key={notification.id}
                                className={`p-5 transition-colors flex gap-4 ${!notification.read
                                    ? (isDark ? 'bg-light/5' : 'bg-teal/5')
                                    : (isDark ? 'hover:bg-light/2' : 'hover:bg-teal/2')
                                    }`}
                            >
                                <div className={`p-2 rounded-full h-fit flex-shrink-0 ${isDark ? 'bg-navy border border-light/10' : 'bg-white border border-teal/10'
                                    }`}>
                                    <span className={`material-symbols-outlined ${getNotificationColor(notification.type)}`}>
                                        {getNotificationIcon(notification.type)}
                                    </span>
                                </div>

                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <h3 className={`font-semibold ${!notification.read ? '' : 'opacity-80'}`}>
                                            {notification.title}
                                        </h3>
                                        <span className={`text-xs whitespace-nowrap ml-4 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>
                                            {formatTimeAgo(notification.created_at)}
                                        </span>
                                    </div>
                                    <p className={`text-sm mt-1 ${isDark ? 'text-light/70' : 'text-darkTeal/70'}`}>
                                        {notification.message}
                                    </p>
                                </div>

                                {!notification.read && (
                                    <button
                                        onClick={() => markAsRead(notification.id)}
                                        className="self-center p-2 rounded-full hover:bg-current/10 transition-colors"
                                        title="Mark as read"
                                    >
                                        <span className="material-symbols-outlined text-lg opacity-60">check_circle</span>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Notifications;
